import {
  type Address,
  type Hash,
  type Hex,
  encodeAbiParameters,
  encodePacked,
  keccak256,
  getAddress,
  decodeAbiParameters,
} from 'viem';
import {
  DERC20Bytecode,
  DERC2080Bytecode,
  DopplerDN404Bytecode,
  DopplerDN404BaseSepoliaBytecode,
} from '../abis';

const DEFAULT_MAX_ITERATIONS = 1_000_000;

export type TokenVariant =
  | 'standard'
  | 'standard-v2'
  | 'dopplerERC20V1'
  | 'doppler404';

// TokenFactory80 has the same deterministic CREATE2 address across all chains where it is deployed.
const TOKEN_FACTORY_80_ADDRESS =
  '0xf0B5141dD9096254B2ca624dff26024f46087229' as const;
const BASE_SEPOLIA_DOPPLER404_FACTORY =
  '0x98b0Aa2e0f134dbB3eb157b5646D387E6D55243a' as const;

function isTokenFactory80(tokenFactory: Address): boolean {
  return tokenFactory.toLowerCase() === TOKEN_FACTORY_80_ADDRESS.toLowerCase();
}

export function getDopplerDN404Bytecode(tokenFactory: Address): Hex {
  return tokenFactory.toLowerCase() ===
    BASE_SEPOLIA_DOPPLER404_FACTORY.toLowerCase()
    ? DopplerDN404BaseSepoliaBytecode
    : DopplerDN404Bytecode;
}

export interface TokenAddressHookConfig {
  deployer: Address;
  initCodeHash: Hash;
  prefix?: string;
  suffix?: string;
}

export interface TokenAddressMiningParams {
  /**
   * Vanity prefix (hex). Keep as `''` (empty string) if you only want to mine a suffix.
   * Note: this is required for backwards compatibility with older SDK versions.
   */
  prefix: string;
  /** Vanity suffix (hex), useful as an identifier at the end of the address. */
  suffix?: string;
  tokenFactory: Address;
  initialSupply: bigint;
  recipient: Address;
  owner: Address;
  tokenData: Hex;
  tokenVariant?: TokenVariant;
  customBytecode?: Hex;
  /**
   * Implementation address for token templates deployed as Solady clones.
   * Used by `standard-v2` and `dopplerERC20V1`.
   */
  v2Implementation?: Address;
  maxIterations?: number;
  startSalt?: bigint;
  hook?: TokenAddressHookConfig;
}

export interface TokenAddressMiningResult {
  salt: Hash;
  tokenAddress: Address;
  iterations: number;
  hookAddress?: Address;
}

const STANDARD_TOKEN_DATA_ABI = [
  { type: 'string' },
  { type: 'string' },
  { type: 'uint256' },
  { type: 'uint256' },
  { type: 'address[]' },
  { type: 'uint256[]' },
  { type: 'string' },
] as const;

const STANDARD_TOKEN_V2_DATA_ABI = [
  { type: 'string' },
  { type: 'string' },
  { type: 'uint256' },
  {
    type: 'tuple[]',
    components: [
      { type: 'uint64', name: 'cliff' },
      { type: 'uint64', name: 'duration' },
    ],
  },
  { type: 'address[]' },
  { type: 'uint256[]' },
  { type: 'uint256[]' },
  { type: 'string' },
] as const;

const DOPPLER_ERC20_V1_TOKEN_DATA_ABI = [
  { type: 'string' },
  { type: 'string' },
  {
    type: 'tuple[]',
    components: [
      { type: 'uint64', name: 'cliff' },
      { type: 'uint64', name: 'duration' },
    ],
  },
  { type: 'address[]' },
  { type: 'uint256[]' },
  { type: 'uint256[]' },
  { type: 'string' },
  { type: 'uint256' },
  { type: 'uint48' },
  { type: 'address' },
  { type: 'address[]' },
] as const;

const DOPPLER404_TOKEN_DATA_ABI = [
  { type: 'string' },
  { type: 'string' },
  { type: 'string' },
  { type: 'uint256' },
] as const;

function normalizeHexFragment(
  value: string,
  label: 'prefix' | 'suffix',
): string {
  const normalized = value.trim().toLowerCase().replace(/^0x/, '');
  if (normalized.length === 0) {
    throw new Error(
      `TokenAddressMiner: ${label} must contain at least one hex character`,
    );
  }
  if (normalized.length > 40) {
    throw new Error(
      `TokenAddressMiner: ${label} cannot exceed 40 hex characters`,
    );
  }
  if (!/^[0-9a-f]+$/i.test(normalized)) {
    throw new Error(`TokenAddressMiner: ${label} must be a hexadecimal string`);
  }
  return normalized;
}

/**
 * Helper to convert hex string to Uint8Array
 */
function hexToBytes(hex: string): Uint8Array {
  const cleanHex = hex.startsWith('0x') ? hex.slice(2) : hex;
  const bytes = new Uint8Array(cleanHex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(cleanHex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/**
 * Helper to convert Uint8Array to hex string
 */
function bytesToHex(bytes: Uint8Array): string {
  return (
    '0x' +
    Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  );
}

/**
 * Pre-compute CREATE2 buffer with constant prefix for fast mining
 * Buffer layout: 0xff (1 byte) + deployer (20 bytes) + salt (32 bytes) + initCodeHash (32 bytes) = 85 bytes
 */
function prepareCreate2Buffer(
  deployer: Address,
  initCodeHash: Hash,
): Uint8Array {
  const buffer = new Uint8Array(85);
  buffer[0] = 0xff;
  const deployerBytes = hexToBytes(deployer);
  buffer.set(deployerBytes, 1);
  const initCodeHashBytes = hexToBytes(initCodeHash);
  buffer.set(initCodeHashBytes, 53); // 1 + 20 + 32 = 53
  return buffer;
}

/**
 * Update salt in pre-computed CREATE2 buffer (bytes 21-52)
 * Uses direct byte manipulation instead of string conversion
 */
function updateSaltInBuffer(buffer: Uint8Array, salt: bigint): void {
  // Salt is 32 bytes, positioned at offset 21 (after 0xff + 20-byte deployer)
  // Clear salt region first
  for (let i = 21; i < 53; i++) {
    buffer[i] = 0;
  }
  // Write salt bytes from right to left (big-endian)
  let remaining = salt;
  for (let i = 52; remaining > 0n && i >= 21; i--) {
    buffer[i] = Number(remaining & 0xffn);
    remaining >>= 8n;
  }
}

/**
 * Compute CREATE2 address from pre-computed buffer (fast version for mining)
 * Returns raw lowercase address without checksum for comparison
 */
function computeCreate2AddressFast(buffer: Uint8Array): string {
  const hash = keccak256(bytesToHex(buffer) as Hex);
  // Return last 40 hex chars (20 bytes) as lowercase address
  return '0x' + hash.slice(-40).toLowerCase();
}

function computeSoladyCloneInitCodeHash(implementation: Address): Hash {
  return keccak256(
    `0x602c3d8160093d39f33d3d3d3d363d3d37363d73${implementation.slice(
      2,
    )}5af43d3d93803e602a57fd5bf3`,
  ) as Hash;
}

function buildTokenInitHash(params: {
  variant: TokenVariant;
  tokenFactory: Address;
  tokenData: Hex;
  initialSupply: bigint;
  recipient: Address;
  owner: Address;
  customBytecode?: Hex;
  v2Implementation?: Address;
}): Hash {
  const {
    variant,
    tokenFactory,
    tokenData,
    initialSupply,
    recipient,
    owner,
    customBytecode,
    v2Implementation,
  } = params;

  if (variant === 'doppler404') {
    const [name, symbol, baseURI, unit] = decodeAbiParameters(
      DOPPLER404_TOKEN_DATA_ABI,
      tokenData,
    );

    const initHashData = encodeAbiParameters(
      [
        { type: 'string' },
        { type: 'string' },
        { type: 'uint256' },
        { type: 'address' },
        { type: 'address' },
        { type: 'string' },
        { type: 'uint256' },
      ],
      [name, symbol, initialSupply, recipient, owner, baseURI, unit],
    );

    return keccak256(
      encodePacked(
        ['bytes', 'bytes'],
        [customBytecode ?? getDopplerDN404Bytecode(tokenFactory), initHashData],
      ),
    ) as Hash;
  }

  if (variant === 'standard-v2') {
    decodeAbiParameters(STANDARD_TOKEN_V2_DATA_ABI, tokenData);
    if (!v2Implementation) {
      throw new Error(
        'TokenAddressMiner: v2Implementation is required for standard-v2 tokens',
      );
    }

    return computeSoladyCloneInitCodeHash(v2Implementation);
  }

  if (variant === 'dopplerERC20V1') {
    decodeAbiParameters(DOPPLER_ERC20_V1_TOKEN_DATA_ABI, tokenData);
    // `v2Implementation` is the shared clone implementation input for both
    // CloneDERC20VotesV2 and DopplerERC20V1 token factories.
    if (!v2Implementation) {
      throw new Error(
        'TokenAddressMiner: v2Implementation is required for dopplerERC20V1 tokens',
      );
    }

    return computeSoladyCloneInitCodeHash(v2Implementation);
  }

  const [
    name,
    symbol,
    yearlyMintRate,
    vestingDuration,
    vestingRecipients,
    vestingAmounts,
    tokenURI,
  ] = decodeAbiParameters(STANDARD_TOKEN_DATA_ABI, tokenData) as readonly [
    string,
    string,
    bigint,
    bigint,
    readonly Address[],
    readonly bigint[],
    string,
  ];

  const initHashData = encodeAbiParameters(
    [
      { type: 'string' },
      { type: 'string' },
      { type: 'uint256' },
      { type: 'address' },
      { type: 'address' },
      { type: 'uint256' },
      { type: 'uint256' },
      { type: 'address[]' },
      { type: 'uint256[]' },
      { type: 'string' },
    ],
    [
      name,
      symbol,
      initialSupply,
      recipient,
      owner,
      yearlyMintRate,
      vestingDuration,
      Array.from(vestingRecipients),
      Array.from(vestingAmounts),
      tokenURI,
    ],
  );

  const defaultBytecode = isTokenFactory80(tokenFactory)
    ? (DERC2080Bytecode as Hex)
    : (DERC20Bytecode as Hex);

  return keccak256(
    encodePacked(
      ['bytes', 'bytes'],
      [customBytecode ?? defaultBytecode, initHashData],
    ),
  ) as Hash;
}

export function mineTokenAddress(
  params: TokenAddressMiningParams,
): TokenAddressMiningResult {
  const {
    prefix,
    suffix,
    tokenFactory,
    initialSupply,
    recipient,
    owner,
    tokenData,
    tokenVariant = 'standard',
    customBytecode,
    maxIterations = DEFAULT_MAX_ITERATIONS,
    startSalt = 0n,
    hook,
  } = params;

  if (maxIterations <= 0 || !Number.isFinite(maxIterations)) {
    throw new Error(
      'TokenAddressMiner: maxIterations must be a positive finite number',
    );
  }
  if (startSalt < 0n) {
    throw new Error('TokenAddressMiner: startSalt cannot be negative');
  }

  const prefixTrimmed = prefix.trim();
  const suffixTrimmed = suffix?.trim();

  const normalizedPrefix =
    prefixTrimmed.length === 0
      ? undefined
      : normalizeHexFragment(prefixTrimmed, 'prefix');
  const normalizedSuffix =
    !suffixTrimmed || suffixTrimmed.length === 0
      ? undefined
      : normalizeHexFragment(suffixTrimmed, 'suffix');

  if (!normalizedPrefix && !normalizedSuffix) {
    throw new Error('TokenAddressMiner: must provide prefix and/or suffix');
  }

  const tokenInitHash = buildTokenInitHash({
    variant: tokenVariant,
    tokenFactory,
    tokenData,
    initialSupply,
    recipient,
    owner,
    customBytecode,
    v2Implementation: params.v2Implementation,
  });

  const hookConfig = hook
    ? {
        deployer: hook.deployer,
        initCodeHash: hook.initCodeHash,
        prefix:
          hook.prefix && hook.prefix.trim().length > 0
            ? normalizeHexFragment(hook.prefix, 'prefix')
            : undefined,
        suffix:
          hook.suffix && hook.suffix.trim().length > 0
            ? normalizeHexFragment(hook.suffix, 'suffix')
            : undefined,
      }
    : undefined;

  const maxSalt = startSalt + BigInt(maxIterations);
  let iterations = 0;

  // Pre-allocate CREATE2 buffer with constant parts (optimization)
  const tokenBuffer = prepareCreate2Buffer(tokenFactory, tokenInitHash);
  const hookBuffer = hookConfig
    ? prepareCreate2Buffer(hookConfig.deployer, hookConfig.initCodeHash)
    : null;

  for (let salt = startSalt; salt < maxSalt; salt++) {
    // Update salt in pre-computed buffer (avoids string formatting)
    updateSaltInBuffer(tokenBuffer, salt);

    // Compute token address using fast method (no checksum)
    const candidateRaw = computeCreate2AddressFast(tokenBuffer);
    const candidateHex = candidateRaw.slice(2);
    iterations++;

    if (normalizedPrefix && !candidateHex.startsWith(normalizedPrefix))
      continue;
    if (normalizedSuffix && !candidateHex.endsWith(normalizedSuffix)) continue;

    let hookAddressRaw: string | undefined;
    if (hookBuffer) {
      updateSaltInBuffer(hookBuffer, salt);
      hookAddressRaw = computeCreate2AddressFast(hookBuffer);
      const hookHex = hookAddressRaw.slice(2);
      if (hookConfig?.prefix && !hookHex.startsWith(hookConfig.prefix))
        continue;
      if (hookConfig?.suffix && !hookHex.endsWith(hookConfig.suffix)) continue;
    }

    // Found a match! Convert to proper format for return
    const saltHex = `0x${salt.toString(16).padStart(64, '0')}` as Hash;
    return {
      salt: saltHex,
      tokenAddress: getAddress(candidateRaw) as Address,
      iterations,
      hookAddress: hookAddressRaw
        ? (getAddress(hookAddressRaw) as Address)
        : undefined,
    };
  }

  const constraints: string[] = [];
  if (normalizedPrefix) constraints.push(`prefix ${prefixTrimmed}`);
  if (normalizedSuffix) constraints.push(`suffix ${suffixTrimmed}`);

  throw new Error(
    `TokenAddressMiner: could not find salt matching ${constraints.join(
      ' and ',
    )} within ${maxIterations} iterations`,
  );
}
